import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api'
import type {
  ClienteAdmin,
  ClienteEntrada,
  DetalleProyectoAdmin,
  IntegranteEntrada,
  IntegranteEquipo,
  InvitacionEntrada,
  PilarAdmin,
  PilarEntrada,
  Profesional,
  ProyectoAdmin,
  ProyectoEntrada,
  ResultadoInvitacion,
} from '@/lib/schemas/admin'
import { CLAVE_SESION } from '@/features/auth/useSesion'

/**
 * Lecturas y escrituras de Administración sobre /api/admin. Cada escritura
 * invalida lo que muestra la pantalla; la sesión también, porque la lista de
 * proyectos del selector sale de ahí.
 */
const CLAVES = {
  clientes: ['admin', 'clientes'] as const,
  proyectos: (clienteId: string) => ['admin', 'proyectos', clienteId] as const,
  detalle: (proyectoId: string) => ['admin', 'detalle', proyectoId] as const,
  equipo: ['admin', 'equipo'] as const,
  profesionales: ['admin', 'profesionales'] as const,
}

const jsonInit = (metodo: 'POST' | 'PUT', cuerpo: unknown): RequestInit => ({
  method: metodo,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(cuerpo),
})

/* ── Lecturas ───────────────────────────────────────────── */

export function useClientes() {
  const api = useApi()
  return useQuery({
    queryKey: CLAVES.clientes,
    queryFn: () => api<ClienteAdmin[]>('/api/admin/clientes'),
  })
}

export function useProyectosDeCliente(clienteId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: CLAVES.proyectos(clienteId ?? ''),
    queryFn: () =>
      api<ProyectoAdmin[]>(`/api/admin/clientes/${clienteId ?? ''}/proyectos`),
    enabled: Boolean(clienteId),
  })
}

export function useDetalleProyecto(proyectoId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: CLAVES.detalle(proyectoId ?? ''),
    queryFn: () => api<DetalleProyectoAdmin>(`/api/admin/proyectos/${proyectoId ?? ''}`),
    enabled: Boolean(proyectoId),
  })
}

export function useEquipo(habilitado: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: CLAVES.equipo,
    queryFn: () => api<IntegranteEquipo[]>('/api/admin/equipo'),
    enabled: habilitado,
  })
}

export function useProfesionales() {
  const api = useApi()
  return useQuery({
    queryKey: CLAVES.profesionales,
    queryFn: () => api<Profesional[]>('/api/admin/profesionales'),
  })
}

/* ── Escrituras ─────────────────────────────────────────── */

function useInvalidar() {
  const qc = useQueryClient()
  return {
    clientes: () => qc.invalidateQueries({ queryKey: CLAVES.clientes }),
    proyectos: (clienteId: string) =>
      qc.invalidateQueries({ queryKey: CLAVES.proyectos(clienteId) }),
    detalle: (proyectoId: string) =>
      qc.invalidateQueries({ queryKey: CLAVES.detalle(proyectoId) }),
    equipo: () => qc.invalidateQueries({ queryKey: CLAVES.equipo }),
    profesionales: () => qc.invalidateQueries({ queryKey: CLAVES.profesionales }),
    sesion: () => qc.invalidateQueries({ queryKey: CLAVE_SESION }),
    todoDetalle: () => qc.invalidateQueries({ queryKey: ['admin', 'detalle'] }),
  }
}

export function useGuardarCliente() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id, datos }: { id: string | null; datos: ClienteEntrada }) =>
      id
        ? api<ClienteAdmin>(`/api/admin/clientes/${id}`, jsonInit('PUT', datos))
        : api<ClienteAdmin>('/api/admin/clientes', jsonInit('POST', datos)),
    onSuccess: async () => {
      await Promise.all([
        invalidar.clientes(),
        invalidar.sesion(),
        invalidar.todoDetalle(),
      ])
    },
  })
}

export function useGuardarProyecto() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id, datos }: { id: string | null; datos: ProyectoEntrada }) =>
      id
        ? api<ProyectoAdmin>(`/api/admin/proyectos/${id}`, jsonInit('PUT', datos))
        : api<ProyectoAdmin>('/api/admin/proyectos', jsonInit('POST', datos)),
    onSuccess: async (p) => {
      await Promise.all([
        invalidar.proyectos(p.clienteId),
        invalidar.detalle(p.id),
        invalidar.clientes(),
        invalidar.sesion(),
      ])
    },
  })
}

export function useActivarProyecto() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      api<ProyectoAdmin>(
        `/api/admin/proyectos/${id}/activo`,
        jsonInit('POST', { activo }),
      ),
    onSuccess: async (p) => {
      await Promise.all([
        invalidar.proyectos(p.clienteId),
        invalidar.detalle(p.id),
        invalidar.clientes(),
        invalidar.sesion(),
      ])
    },
  })
}

export function useGuardarPilar() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id, datos }: { id: string | null; datos: PilarEntrada }) =>
      id
        ? api<PilarAdmin>(`/api/admin/pilares/${id}`, jsonInit('PUT', datos))
        : api<PilarAdmin>('/api/admin/pilares', jsonInit('POST', datos)),
    onSuccess: (p) => invalidar.detalle(p.proyectoId),
  })
}

export function useDarDeBajaPilar() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id }: { id: string; proyectoId: string }) =>
      api<null>(`/api/admin/pilares/${id}/baja`, { method: 'POST' }),
    onSuccess: (_r, v) => invalidar.detalle(v.proyectoId),
  })
}

export function useInvitarPersona() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (datos: InvitacionEntrada) =>
      api<ResultadoInvitacion>('/api/admin/invitaciones', jsonInit('POST', datos)),
    onSuccess: async (_r, datos) => {
      await Promise.all(datos.proyectoIds.map((id) => invalidar.detalle(id)))
    },
  })
}

export function useMailYaEsUsuario() {
  const api = useApi()
  return async (email: string) => {
    const r = await api<{ existe: boolean }>(
      `/api/admin/invitaciones/existe?email=${encodeURIComponent(email)}`,
    )
    return r.existe
  }
}

export function useQuitarAcceso() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (datos: { usuarioId: string; proyectoId: string }) =>
      api<null>('/api/admin/accesos/quitar', jsonInit('POST', datos)),
    onSuccess: (_r, v) => invalidar.detalle(v.proyectoId),
  })
}

/** DEC-10: el mail de invitación otra vez, desde la casilla de la asignada. */
export function useReenviarInvitacion() {
  const api = useApi()
  return useMutation({
    mutationFn: (datos: { usuarioId: string; proyectoId: string }) =>
      api<{ mail: 'enviado' | 'sin_clave' | 'error' }>(
        '/api/admin/accesos/reenviar',
        jsonInit('POST', datos),
      ),
  })
}

export function useAgregarIntegrante() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (datos: IntegranteEntrada) =>
      api<IntegranteEquipo>('/api/admin/equipo', jsonInit('POST', datos)),
    onSuccess: () => Promise.all([invalidar.equipo(), invalidar.profesionales()]),
  })
}

export function useQuitarIntegrante() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (id: string) =>
      api<null>(`/api/admin/equipo/${id}/quitar`, { method: 'POST' }),
    onSuccess: () => Promise.all([invalidar.equipo(), invalidar.profesionales()]),
  })
}
